import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { speakText, getVoiceGender, setVoiceGenderPref, warmUpVoices, sttLang } from "@/lib/voice";
import { useLang } from "@/contexts/LanguageContext";
import { Sparkles, X, Send, Loader2, MessageSquareText, Mic, MicOff, Volume2, VolumeX, User } from "lucide-react";

/**
 * AskFRBubble — landing-page-only AI assistant. Warm-gold sparkle FAB at the
 * bottom-left that expands into a chat window. Uses the public /api/chatbot/ask
 * endpoint (no auth). Voice: choose Male/Female reply voice; when you speak,
 * the mic keeps listening through pauses and, when you stop, shows the text
 * with a ~30s "Send now" countdown before auto-sending.
 */

const FALLBACK_SUGGESTIONS = [
    "What is Family Repository?",
    "How do I start?",
    "Is my data private?",
    "How much does it cost?",
];

const AUTO_SEND_SECONDS = 30;
const pillLabel = (q) => (q.length > 42 ? q.slice(0, 40).trimEnd() + "…" : q);

const SpeechRecognition = typeof window !== "undefined"
    ? (window.SpeechRecognition || window.webkitSpeechRecognition)
    : null;

export default function AskFRBubble() {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [input, setInput] = useState("");
    const [sessionId, setSessionId] = useState(null);
    const [listening, setListening] = useState(false);
    const [speakBack, setSpeakBack] = useState(false);
    const [voiceGender, setVoiceGender] = useState(getVoiceGender);
    const [pending, setPending] = useState(null); // {seconds} — voice review countdown
    const [suggestions, setSuggestions] = useState(FALLBACK_SUGGESTIONS);
    const [messages, setMessages] = useState([
        { role: "bot", text: "Namaste 🙏 I'm Ask FR — your 24×7 guide to Family Repository. Ask me anything about the platform, features, or how to preserve your family's legacy." },
    ]);
    const scrollRef = useRef(null);
    const recogRef = useRef(null);
    const finalRef = useRef("");
    const tickRef = useRef(null);
    const maxRef = useRef(null);
    const genderRef = useRef(voiceGender);
    const { lang } = useLang();
    const langRef = useRef(lang);

    useEffect(() => { genderRef.current = voiceGender; }, [voiceGender]);
    useEffect(() => { langRef.current = lang; }, [lang]);
    useEffect(() => { warmUpVoices(); }, []);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, open]);

    useEffect(() => {
        let alive = true;
        api.get("/chatbot/popular?limit=6")
            .then(({ data }) => {
                if (alive && Array.isArray(data.items) && data.items.length) setSuggestions(data.items);
            })
            .catch(() => {});
        return () => { alive = false; };
    }, []);

    const cycleGender = () => setVoiceGender((g) => {
        const n = g === "male" ? "female" : "male";
        setVoiceGenderPref(n);
        return n;
    });

    const speak = useCallback((text) => speakText(text, genderRef.current, sttLang(langRef.current)), []);

    const clearPending = useCallback(() => {
        if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
        setPending(null);
    }, []);

    const send = useCallback(async (text, viaVoice = false) => {
        const q = (text ?? input).trim();
        if (!q || busy) return;
        clearPending();
        setInput("");
        setMessages((m) => [...m, { role: "user", text: q }]);
        setBusy(true);
        try {
            const { data } = await api.post("/chatbot/ask", { session_id: sessionId, message: q });
            if (data.session_id && !sessionId) setSessionId(data.session_id);
            setMessages((m) => [...m, { role: "bot", text: data.reply || "Sorry, I couldn't answer that. Please try again.", followups: data.followups || [] }]);
            if (speakBack || viaVoice) speak(data.reply);
        } catch (e) {
            setMessages((m) => [...m, { role: "bot", text: "I'm having trouble reaching my AI brain right now. Please try again in a few seconds — or visit /support." }]);
        } finally {
            setBusy(false);
        }
    }, [input, busy, sessionId, speak, speakBack, clearPending]);

    // After voice capture, don't fire immediately — show the text and count down.
    const startPending = useCallback((text) => {
        clearPending();
        let s = AUTO_SEND_SECONDS;
        setPending({ seconds: s });
        tickRef.current = setInterval(() => {
            s -= 1;
            if (s <= 0) { clearPending(); send(text, true); }
            else setPending({ seconds: s });
        }, 1000);
    }, [clearPending, send]);

    const toggleMic = useCallback(() => {
        if (!SpeechRecognition) return;
        if (listening) { try { recogRef.current?.stop(); } catch { /* */ } return; }
        try {
            const r = new SpeechRecognition();
            r.lang = sttLang(langRef.current);
            r.interimResults = true;
            r.continuous = true; // keep listening through pauses
            r.maxAlternatives = 1;
            finalRef.current = "";
            clearPending();
            r.onresult = (ev) => {
                let interim = "";
                for (let i = ev.resultIndex; i < ev.results.length; i++) {
                    const t = ev.results[i][0].transcript;
                    if (ev.results[i].isFinal) finalRef.current += t; else interim += t;
                }
                setInput((finalRef.current + interim).trim());
            };
            r.onend = () => {
                setListening(false);
                if (maxRef.current) { clearTimeout(maxRef.current); maxRef.current = null; }
                const said = finalRef.current.trim();
                if (said) startPending(said);
            };
            r.onerror = () => setListening(false);
            recogRef.current = r;
            setListening(true);
            r.start();
            maxRef.current = setTimeout(() => { try { r.stop(); } catch { /* */ } }, 30000);
        } catch { setListening(false); }
    }, [listening, startPending, clearPending]);

    useEffect(() => () => { clearPending(); if (maxRef.current) clearTimeout(maxRef.current); }, [clearPending]);

    return (
        <>
            {/* FAB — warm gold sparkle icon, bottom-left */}
            {!open && (
                <button
                    type="button"
                    data-testid="askfr-fab"
                    aria-label="Ask FR — AI assistant"
                    onClick={() => setOpen(true)}
                    className="fixed bottom-5 left-4 sm:bottom-6 sm:left-6 z-[65] h-14 w-14 rounded-full flex items-center justify-center shadow-[0_12px_36px_rgba(245,158,11,0.5)] hover:shadow-[0_16px_44px_rgba(245,158,11,0.65)] transition-all hover:-translate-y-1 group"
                    style={{ background: "conic-gradient(from 180deg at 50% 50%, #F59E0B, #FBBF24, #EA580C, #F59E0B)" }}
                >
                    <span className="absolute inset-0 rounded-full bg-gradient-to-br from-[#F59E0B] to-[#EA580C] opacity-90" />
                    <span className="absolute inset-[3px] rounded-full bg-white/10 backdrop-blur-sm" />
                    <Sparkles className="relative h-6 w-6 text-white drop-shadow-md group-hover:rotate-12 transition-transform" strokeWidth={2.4} />
                    <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-[#22C55E] ring-2 ring-white animate-pulse" />
                </button>
            )}

            {/* Chat window */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: 24, scale: 0.94 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 24, scale: 0.94 }}
                        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                        className="fixed bottom-5 left-4 sm:bottom-6 sm:left-6 z-[65] w-[92vw] sm:w-[380px] h-[540px] max-h-[85vh] bg-white rounded-3xl shadow-[0_28px_80px_rgba(15,23,42,0.28)] border border-[#E5E7EB] flex flex-col overflow-hidden"
                        data-testid="askfr-window"
                    >
                        {/* Header */}
                        <div className="relative px-4 py-3 flex items-center gap-2" style={{ background: "linear-gradient(135deg, #EA580C 0%, #F59E0B 50%, #FBBF24 100%)" }}>
                            <div className="h-9 w-9 rounded-full bg-white/25 backdrop-blur-sm flex items-center justify-center shadow-inner">
                                <Sparkles className="h-5 w-5 text-white" strokeWidth={2.4} />
                            </div>
                            <div className="flex-1 min-w-0 text-white">
                                <div className="font-extrabold text-[15px] leading-tight tracking-tight">Ask FR</div>
                                <div className="text-[10.5px] text-white/85 flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-[#22C55E] ring-1 ring-white/40" /> 24×7 Knowledge Assistant</div>
                            </div>
                            <button type="button" onClick={cycleGender} data-testid="askfr-voice-gender"
                                title={`Voice: ${voiceGender === "male" ? "Male" : "Female"} (tap to switch)`}
                                className="h-8 px-2 rounded-full bg-white/20 hover:bg-white/35 flex items-center gap-1 text-white text-[11px] font-bold transition-colors" aria-label="Switch voice gender">
                                <User className="h-3.5 w-3.5" /> {voiceGender === "male" ? "M" : "F"}
                            </button>
                            <button type="button" onClick={() => setSpeakBack((v) => !v)} data-testid="askfr-speaker"
                                className="h-8 w-8 rounded-full bg-white/20 hover:bg-white/35 flex items-center justify-center text-white transition-colors" aria-label="Toggle voice reply">
                                {speakBack ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                            </button>
                            <button type="button" onClick={() => setOpen(false)} data-testid="askfr-close"
                                className="h-8 w-8 rounded-full bg-white/20 hover:bg-white/35 flex items-center justify-center text-white transition-colors" aria-label="Close chat">
                                <X className="h-4 w-4" />
                            </button>
                        </div>

                        {/* Messages */}
                        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-gradient-to-b from-[#FFFBEB] to-white" data-testid="askfr-messages">
                            {messages.map((m, i) => (
                                <div key={i}>
                                    <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                                        <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed shadow-sm whitespace-pre-wrap ${
                                            m.role === "user"
                                                ? "bg-[#6D28D9] text-white rounded-br-md"
                                                : "bg-white border border-[#F3E8DB] text-[#1F2937] rounded-bl-md"
                                        }`}>
                                            {m.text}
                                        </div>
                                    </div>
                                    {m.role === "bot" && i === messages.length - 1 && !busy && Array.isArray(m.followups) && m.followups.length > 0 && (
                                        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="askfr-followups">
                                            <span className="text-[10px] font-bold uppercase tracking-wider text-[#9A3412] w-full">You may also ask</span>
                                            {m.followups.map((f, j) => (
                                                <button key={`${i}-fu-${j}`} type="button" onClick={() => send(f)} title={f}
                                                    data-testid={`askfr-followup-${j}`}
                                                    className="text-[11.5px] px-2.5 py-1 rounded-full bg-white border border-[#F59E0B]/40 text-[#9A3412] hover:bg-[#FFF7ED] transition-colors">
                                                    {pillLabel(f)}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                            {busy && (
                                <div className="flex justify-start">
                                    <div className="bg-white border border-[#F3E8DB] rounded-2xl rounded-bl-md px-3.5 py-2.5 shadow-sm inline-flex items-center gap-1.5 text-[#6B7280] text-[12px]">
                                        <span className="h-1.5 w-1.5 rounded-full bg-[#F59E0B] animate-bounce" style={{ animationDelay: "0ms" }} />
                                        <span className="h-1.5 w-1.5 rounded-full bg-[#F59E0B] animate-bounce" style={{ animationDelay: "120ms" }} />
                                        <span className="h-1.5 w-1.5 rounded-full bg-[#F59E0B] animate-bounce" style={{ animationDelay: "240ms" }} />
                                    </div>
                                </div>
                            )}
                            {messages.length === 1 && (
                                <div className="pt-2">
                                    <div className="text-[10.5px] font-bold uppercase tracking-wider text-[#9A3412] mb-1.5 flex items-center gap-1">
                                        <MessageSquareText className="h-3 w-3" /> Most asked
                                    </div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {suggestions.map((s, i) => (
                                            <button key={`${i}-${s.slice(0, 8)}`} type="button" onClick={() => send(s)}
                                                title={s}
                                                data-testid={`askfr-suggest-${i}`}
                                                className="text-[11.5px] px-2.5 py-1 rounded-full bg-white border border-[#F59E0B]/40 text-[#9A3412] hover:bg-[#FFF7ED] transition-colors">
                                                {pillLabel(s)}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Composer */}
                        <div className="p-3 border-t border-[#F3E8DB] bg-white">
                            {pending && (
                                <div data-testid="askfr-voice-countdown" className="mb-2 flex items-center justify-between gap-2 rounded-xl bg-[#FFF7ED] border border-[#F59E0B]/40 px-3 py-1.5">
                                    <span className="text-[11px] text-[#9A3412] inline-flex items-center gap-1.5 min-w-0">
                                        <Mic className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">Sending in {pending.seconds}s — review or edit</span>
                                    </span>
                                    <div className="flex items-center gap-1.5 shrink-0">
                                        <button type="button" onClick={clearPending} data-testid="askfr-voice-edit"
                                            className="text-[11px] px-2 py-1 rounded-full bg-white border border-[#E5E7EB] text-[#6B7280] hover:bg-[#F9FAFB]">Edit</button>
                                        <button type="button" onClick={() => send(input, true)} data-testid="askfr-voice-sendnow"
                                            className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-[#EA580C] text-white hover:bg-[#c2410c]">Send now</button>
                                    </div>
                                </div>
                            )}
                            <div className="flex items-end gap-2">
                                {SpeechRecognition && (
                                    <button type="button" onClick={toggleMic} data-testid="askfr-mic"
                                        className={`h-10 w-10 shrink-0 rounded-full flex items-center justify-center transition-all ${listening ? "bg-[#DC2626] text-white animate-pulse" : "bg-[#FFF7ED] text-[#EA580C] hover:bg-[#FFEDD5]"}`}
                                        aria-label={listening ? "Stop listening" : "Speak your question"}>
                                        {listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                                    </button>
                                )}
                                <textarea
                                    data-testid="askfr-input"
                                    value={input}
                                    onChange={(e) => { setInput(e.target.value); if (pending) clearPending(); }}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                                    }}
                                    rows={1}
                                    placeholder={listening ? "Listening…" : "Type or speak your question…"}
                                    className="flex-1 resize-none rounded-2xl border border-[#E5E7EB] focus:border-[#F59E0B] focus:ring-2 focus:ring-[#FBBF24]/30 outline-none px-3.5 py-2 text-[13px] max-h-24"
                                />
                                <button type="button" onClick={() => send()} disabled={busy || !input.trim()}
                                    data-testid="askfr-send"
                                    className="h-10 w-10 shrink-0 rounded-full bg-gradient-to-br from-[#EA580C] to-[#F59E0B] text-white shadow-md hover:shadow-lg disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center">
                                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                </button>
                            </div>
                            <p className="text-[9.5px] text-[#9CA3AF] mt-1.5 text-center">Powered by AI · Never shares OTP / password / card info</p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
